// Types mirroring `apple_grant_status`, the SAFE, secret-free projection of the backend's
// `apple_org_grants` collection. Authoritative source:
// `apple-integration-backend/functions/src/schemas/apple-grant-status.schema.ts`.
//
// The projection exists for one reason: this CMS needs to show whether a client has linked their
// Apple Business Connect account, and it must NEVER read `apple_org_grants` — that collection holds
// the AES-256-GCM-encrypted OAuth access and refresh tokens. Nothing token-shaped appears here, and
// nothing token-shaped may ever be added.

/** Whether the client's OAuth grant is live. Mirrored from the grant. */
export enum GrantStateEnum {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

/**
 * Why a projection document is flagged for staff review. Only one cause exists today: an account
 * bound to more than one ACTIVE Apple company. An enum rather than a boolean so the backend can add
 * a second cause without a migration.
 */
export enum GrantStatusReviewReasonEnum {
  MULTIPLE_ACTIVE_GRANTS = "MULTIPLE_ACTIVE_GRANTS",
}

/** One competing ACTIVE grant, rendered for a human deciding which company is the real client. */
export type GrantStatusConflict = {
  companyId: string;
  legalName?: string | null;
  state: GrantStateEnum;
  createdAt: Date;
};

export type AppleGrantStatusEntity = {
  // Identity — the DearDoc account join and the Apple company it resolved to. `accountId` is also
  // the document id.
  accountId: string;
  companyId: string;
  legalName?: string | null;
  orgId?: string | null;
  salesforce_id?: string | null;
  // Lifecycle.
  state: GrantStateEnum;
  createdAt: Date;
  lastRefreshedAt?: Date | null;
  // Grant-conflict guard. During a conflict the backend refuses every delegated write, and this
  // document's headline identity is the INCUMBENT (oldest ACTIVE) company — the newcomer, which is
  // the one that might be hostile, appears in `conflictingCompanies`.
  needsReview: boolean;
  reviewReason?: GrantStatusReviewReasonEnum | null;
  /** DISTINCT Apple companies holding an ACTIVE grant for this account — not a count of documents. */
  activeCompanyCount: number;
  conflictingCompanies: GrantStatusConflict[];
  conflictDetectedAt?: Date | null;
};

/** Enum labels for the raw grant state, for the FireCMS property dropdown. */
export const GRANT_STATE_LABELS: Record<string, string> = {
  [GrantStateEnum.ACTIVE]: "Active",
  [GrantStateEnum.INACTIVE]: "Inactive",
};

export const REVIEW_REASON_LABELS: Record<string, string> = {
  [GrantStatusReviewReasonEnum.MULTIPLE_ACTIVE_GRANTS]: "Multiple active Apple companies",
};

/**
 * The single status a staff member reads, in plain language. `needsReview` deliberately outranks
 * `state`: during a conflict the backend refuses every delegated write, so showing "Linked" would
 * be a lie.
 */
export const friendlyGrantStatus = (values: Partial<AppleGrantStatusEntity>): string => {
  if (values.needsReview) return "Needs review";
  if (values.state === GrantStateEnum.ACTIVE) return "Linked";
  if (values.state === GrantStateEnum.INACTIVE) return "Not linked";
  return "Unknown";
};

/**
 * Whether a connect link may be generated for this account.
 *
 * Apple's stated best practice: disable the authorization link once you hold a valid access token,
 * because re-authorizing invalidates the live one and silently breaks a working client. A grant in
 * conflict is blocked too — an account already bound to two Apple companies is the last one that
 * should receive a third link.
 */
export const isConnectLinkBlocked = (values: Partial<AppleGrantStatusEntity>): boolean =>
  values.state === GrantStateEnum.ACTIVE || Boolean(values.needsReview);

/** Firestore collection holding the projection. Also the FireCMS collection id and path. */
export const APPLE_GRANT_STATUS_PATH = "apple_grant_status";

/**
 * Names the company on a projection document, for a human.
 *
 * `legalName` is `z.string().nullish()` on the backend schema, so it can be absent on a real
 * document. Never branch on its truthiness to decide whether a grant EXISTS — that conflates "no
 * grant on record" with "a grant whose company name Apple did not give us", and the two need
 * opposite messages.
 */
export const describeLinkedCompany = (
  values: Partial<AppleGrantStatusEntity>,
  fallback: string,
): string => values.legalName ?? values.companyId ?? fallback;
